import { FC, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import ConfigurationHeader from 'modules/configuration/components/configuration-header/ConfigurationHeader';
import { DataList, MessageGuide, MessageModal } from 'common/components';
import AddNewIndicator from 'modules/configuration/components/add-new-indicator/AddNewIndicator';
import { configurationActions } from 'modules/configuration/redux';
import { RootState } from 'common/redux/core/root.reducer';
import {
    getConfigurationDeleteResponse,
    getConfigurationReorderResponse,
    getConfigurationSaveResponse,
    getHierarchicalDataValue,
    getStaticIndicatorsResponse
} from 'modules/configuration/redux/configuration.selector';
import {
    STATIC_INDICATOR_TITLE,
    getConfigurationIndicatorsDataListColDefs
} from 'modules/configuration/config';
import { ApiStatus, ButtonLabel, EntityActions } from 'common/enums';
import { ConfigurationTypes } from 'modules/configuration/enums/configuration.enum';
import {
    ACTION_ITEMS,
    DELETE_POPUP_SUMMARY_PART1,
    DELETE_POPUP_SUMMARY_PART2,
    DELETE_POPUP_TITLE,
    STATIC_INDICATOR_GUIDE
} from 'common/config';
import {
    ConfigurationIndicatorVM,
    ConfigurationTableRow,
    ConfigurationReorderUpdateList,
    ConfigurationSaveRequest,
    DeleteConfigurationRequest
} from 'modules/configuration/models/configuration.model';
import { MAX_ALLOWED_STATIC_INDICATORS } from 'common/config/configuration.module.config';
import { getNewlyAddedIndicator, getRemovedIndicator } from 'common/utils/data';

import './StaticIndicator.scss';
import useStaticIndicatorAccess from 'modules/configuration/roles-access-hooks/useStaticIndicatorAccess';

const StaticIndicator: FC = () => {
    /**
     * State variable to contain search text
     */
    const [searchVal, setSearchVal] = useState<string>('');
    const [isAddNewVisible, setIsAddNewVisible] = useState(false);
    /**
     * Confirmation popup details state variable
     */
    const [popupDetails, setPopupDetails] = useState({ title: '', text: '', type: '' });
    const [isShowPopup, setShowPopup] = useState<boolean>(false);
    const [modalData, setModalData] = useState<string>('');
    const { isSetUpDisabled } = useStaticIndicatorAccess();

    const staticIndicatorResponse = useSelector((state: RootState) =>
        getStaticIndicatorsResponse(state)
    );
    const indicatorDeleteResponse = useSelector((state: RootState) =>
        getConfigurationDeleteResponse(state)
    );
    const indicatorReorderResponse = useSelector((state: RootState) =>
        getConfigurationReorderResponse(state)
    );
    const indicatorSaveResponse = useSelector((state: RootState) =>
        getConfigurationSaveResponse(state)
    );
    const hierarchicalDataResponse = useSelector((state: RootState) =>
        getHierarchicalDataValue(state)
    );

    const isIndicatorDeleteSuccess = indicatorDeleteResponse?.status === ApiStatus.SUCCESS;
    const isIndicatorReorderSuccess = indicatorReorderResponse?.status === ApiStatus.SUCCESS;
    const isIndicatorSaveSuccess = indicatorSaveResponse?.status === ApiStatus.SUCCESS;

    const data: never[] = [];
    const dispatch = useDispatch();

    useEffect(() => {
        fetchStaticIndicatorsList();
    }, []);

    useEffect(() => {
        if (isIndicatorDeleteSuccess) {
            fetchStaticIndicatorsList();
            return () => {
                dispatch(configurationActions.resetDeleteData());
            };
        }
    }, [isIndicatorDeleteSuccess]);

    useEffect(() => {
        if (isIndicatorReorderSuccess) {
            fetchStaticIndicatorsList();
            return () => {
                dispatch(configurationActions.resetReOrderData());
            };
        }
    }, [isIndicatorReorderSuccess]);

    useEffect(() => {
        if (isIndicatorSaveSuccess) {
            fetchStaticIndicatorsList();
            return () => {
                dispatch(configurationActions.resetSaveIndicatorAdd());
            };
        }
    }, [isIndicatorSaveSuccess]);

    const fetchStaticIndicatorsList = () => {
        const apiPayload = {
            configurationType: ConfigurationTypes.StaticIndicator
        };
        dispatch(configurationActions.fetchStaticIndicators(apiPayload));
    };

    const handleDelete = (data: ConfigurationTableRow) => {
        setPopupDetails({
            title: DELETE_POPUP_TITLE,
            text: `${DELETE_POPUP_SUMMARY_PART1} this record. ${DELETE_POPUP_SUMMARY_PART2}`,
            type: EntityActions.Delete
        });
        setModalData(data?.id);
        setShowPopup(true);
    };

    /**
     * Following function will handle clicked event of check box
     * @param isSelected Checkbox status
     * @param data Master/Preview data array
     */
    const handleCheckBoxClicked = (isSelected: boolean, data: any) => {
        dispatch(configurationActions.setCheckboxSelected({ isSelected: false, data }));
    };

    /**
     * This function handle serach event
     * @param event Event information object created by user on search box
     */
    const onSearchChangeHandler = (event: any) => {
        setSearchVal(event.target.value);
    };

    const getData = () => {
        if (searchVal.length > 0) {
            return staticIndicatorResponse.data?.filter((indicator: ConfigurationIndicatorVM) =>
                indicator.name.toLowerCase().includes(searchVal)
            );
        }
        return staticIndicatorResponse.data || [];
    };

    const onRowDragEnd = (e: any) => {
        if (staticIndicatorResponse.data && staticIndicatorResponse.data?.length > 1) {
            const reOrderUpdateList: ConfigurationReorderUpdateList[] = [];
            e.api.forEachNode((node: any) => {
                reOrderUpdateList.push({
                    order: node.rowIndex + 1,
                    id: node.data.id
                });
            });
            dispatch(configurationActions.reOrderData(reOrderUpdateList));
        }
    };

    const popupHandler = (id: string) => {
        if (popupDetails.type == EntityActions.Delete) {
            const apiPayload: DeleteConfigurationRequest = {
                configurationType: ConfigurationTypes.StaticIndicator,
                id: id
            };
            dispatch(configurationActions.deleteData(apiPayload));
        }
        setShowPopup(false);
    };

    const getAllowedCount = () => {
        return MAX_ALLOWED_STATIC_INDICATORS - (staticIndicatorResponse.data || []).length;
    };

    const handleIndicatorAdd = () => {
        const indicatorList: string[] =
            staticIndicatorResponse.data?.map((item: any) => item?.indicatorId) || [];
        const newlyAddedIndicator = getNewlyAddedIndicator(hierarchicalDataResponse, indicatorList);
        const removedIndicator = getRemovedIndicator(hierarchicalDataResponse, indicatorList);
        if (newlyAddedIndicator?.length > 0 || removedIndicator?.length > 0) {
            const apiPayload: ConfigurationSaveRequest<ConfigurationTypes> = {
                configurationType: ConfigurationTypes.StaticIndicator,
                addIndicators: newlyAddedIndicator,
                removeIndicators: removedIndicator
            };
            dispatch(configurationActions.saveIndicatorAdd(apiPayload));
        }
        handleAddNewClose();
    };

    const handleAddNewClose = () => {
        setIsAddNewVisible(false);
        dispatch(configurationActions.clearCheckboxSelected());
    };

    return (
        <div className="route-container">
            <ConfigurationHeader
                headerTitle={STATIC_INDICATOR_TITLE}
                buttonTitle={ACTION_ITEMS.ADD_NEW_BUTTON}
                searchVal={searchVal}
                disableButton={
                    isSetUpDisabled ||
                    (staticIndicatorResponse &&
                        staticIndicatorResponse.data &&
                        staticIndicatorResponse?.data.length >= 10)
                }
                isButtonVisible={true}
                onSearchChangeHandler={onSearchChangeHandler}
                onAddNew={() => setIsAddNewVisible(true)}
            />
            <MessageGuide message={STATIC_INDICATOR_GUIDE} />
            <DataList
                isNoDataVisible={getData()?.length === 0}
                data={getData()}
                columnDefs={getConfigurationIndicatorsDataListColDefs(
                    data,
                    handleDelete,
                    isSetUpDisabled
                )}
                onRowDragEnd={onRowDragEnd}
                suppressDrag={searchVal.length > 0}
            />
            <MessageModal
                headerName={popupDetails.title}
                message={popupDetails.text}
                isVisible={isShowPopup}
                isSecondaryButtonVisible={true}
                primaryButton={ButtonLabel.Ok}
                secondaryButton={ButtonLabel.Cancel}
                onPrimaryAction={() => {
                    popupHandler(modalData);
                }}
                onSecondaryAction={() => {
                    setShowPopup(false);
                }}
            />
            {isAddNewVisible && (
                <AddNewIndicator
                    isVisible={isAddNewVisible}
                    updateSelectedCheckbox={handleCheckBoxClicked}
                    allowedCountOfIndicators={getAllowedCount()}
                    modalDetails={{ title: 'Select indicators', text: '', type: '' }}
                    onSave={handleIndicatorAdd}
                    closePopupHandler={handleAddNewClose}
                    configurationType={ConfigurationTypes.StaticIndicator}
                />
            )}
        </div>
    );
};

export default StaticIndicator;
